/**
 * Activity Trigger Hooks
 *
 * TanStack Query hooks for activity trigger configuration.
 * Used for managing camera-based activity triggers for automated assessments.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import { cameraKeys } from './useCameras';

// Types for activity trigger config
export interface ActivityTriggerConfig {
  id?: string;
  orgId?: string;
  cameraConfigId?: string;
  isEnabled: boolean;
  altaRuleNames: string[];
  sopId: string | null;
  sop?: { id: string; title: string } | null;
  captureDurationMinutes: number;
  captureOffsetSeconds: number;
  cooldownMinutes: number;
  maxTriggersPerShift: number;
  activeStartHour: number;
  activeEndHour: number;
  activeDaysOfWeek: string;
  timezone?: string;
  lastTriggeredAt: string | null;
  triggersToday: number;
  triggersResetAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  stats?: ActivityTriggerStats | null;
}

export interface ActivityTriggerStats {
  isEnabled: boolean;
  triggersToday: number;
  maxTriggersPerShift: number;
  lastTriggeredAt: string | null;
  cooldownRemaining: number; // milliseconds
  triggersRemaining: number;
}

export interface UpdateActivityTriggerConfig {
  isEnabled?: boolean;
  altaRuleNames?: string[];
  sopId?: string | null;
  captureDurationMinutes?: number;
  captureOffsetSeconds?: number;
  cooldownMinutes?: number;
  maxTriggersPerShift?: number;
  activeStartHour?: number;
  activeEndHour?: number;
  activeDaysOfWeek?: string;
  timezone?: string;
}

export interface TestTriggerResult {
  triggerId: string;
  operatorId: string;
  sopId: string;
  startTime: string;
  endTime: string;
}

// Query keys
export const activityTriggerKeys = {
  all: ['activity-triggers'] as const,
  config: (cameraId: string) => [...activityTriggerKeys.all, 'config', cameraId] as const,
};

/**
 * Fetch activity trigger config for a camera
 */
export function useActivityTriggerConfig(cameraId: string) {
  const api = useApi();

  return useQuery({
    queryKey: activityTriggerKeys.config(cameraId),
    queryFn: () =>
      api.get<ActivityTriggerConfig>(`/api/cameras/${cameraId}/activity-trigger`),
    enabled: !!cameraId,
  });
}

/**
 * Update activity trigger config mutation
 */
export function useUpdateActivityTriggerConfig() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cameraId,
      data,
    }: {
      cameraId: string;
      data: UpdateActivityTriggerConfig;
    }) =>
      api.put<ActivityTriggerConfig>(
        `/api/cameras/${cameraId}/activity-trigger`,
        data
      ),
    onSuccess: (_, variables) => {
      // Invalidate both activity trigger config and camera details
      queryClient.invalidateQueries({
        queryKey: activityTriggerKeys.config(variables.cameraId),
      });
      queryClient.invalidateQueries({
        queryKey: cameraKeys.detail(variables.cameraId),
      });
    },
  });
}

/**
 * Test activity trigger mutation
 * Manually creates a test trigger to verify the pipeline works
 */
export function useTestActivityTrigger() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cameraId: string) =>
      api.post<TestTriggerResult>(
        `/api/cameras/${cameraId}/activity-trigger/test`,
        {}
      ),
    onSuccess: (_, cameraId) => {
      // Refresh config to see updated stats
      queryClient.invalidateQueries({
        queryKey: activityTriggerKeys.config(cameraId),
      });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Format cooldown time remaining for display
 */
export function formatCooldownRemaining(ms: number): string {
  if (ms <= 0) return 'Ready';

  const minutes = Math.ceil(ms / 60000);
  if (minutes < 60) {
    return `${minutes}m remaining`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m remaining`;
}

/**
 * Parse active days string into day names
 */
export function parseActiveDays(daysString: string): string[] {
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayNumbers = daysString.split(',').map((d) => parseInt(d.trim(), 10));
  return dayNumbers.map((n) => dayNames[n]).filter(Boolean);
}

/**
 * Format active days for display
 */
export function formatActiveDays(daysString: string): string {
  const days = parseActiveDays(daysString);

  // Check for common patterns
  if (days.length === 7) return 'Every day';
  if (days.length === 5 && !days.includes('Sat') && !days.includes('Sun')) {
    return 'Weekdays';
  }
  if (days.length === 2 && days.includes('Sat') && days.includes('Sun')) {
    return 'Weekends';
  }

  return days.join(', ');
}

/**
 * Format hour for display (24h to 12h)
 */
export function formatHour(hour: number): string {
  if (hour === 0) return '12:00 AM';
  if (hour === 12) return '12:00 PM';
  if (hour < 12) return `${hour}:00 AM`;
  return `${hour - 12}:00 PM`;
}

/**
 * Format time window for display with optional timezone abbreviation
 */
export function formatTimeWindow(startHour: number, endHour: number, timezone?: string): string {
  const window = `${formatHour(startHour)} - ${formatHour(endHour)}`;
  if (!timezone) return window;
  try {
    const abbr = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value ?? '';
    return abbr ? `${window} ${abbr}` : window;
  } catch {
    return window;
  }
}
